import { useId, useState, type ComponentType, type ReactNode } from 'react';
import type { LucideProps } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type Tab = {
  key: string;
  label: string;
  icon?: ComponentType<LucideProps>;
  content: ReactNode;
};

type Props = {
  tabs: Tab[];
  /** Initial tab key when the URL has no `tab` param. */
  initialKey?: string;
  /**
   * If set, the active tab is reflected in the URL as ?{paramName}={key}.
   * Use a distinct name (e.g. "tab") so multiple Tabs instances don't collide.
   */
  paramName?: string;
  /**
   * When true, tabs that supply an icon hide their label below md so a long
   * strip (10+ tabs on client detail) still fits without horizontal scroll.
   * Inactive icon-only tabs get the label as an aria-label fallback.
   */
  iconOnlyOnMobile?: boolean;
  className?: string;
};

export function Tabs({ tabs, initialKey, paramName, iconOnlyOnMobile, className }: Props) {
  const groupId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = new Set(tabs.map((t) => t.key));
  const fallback = initialKey && validKeys.has(initialKey) ? initialKey : tabs[0]?.key;

  const [localActive, setLocalActive] = useState(fallback);
  const urlActive = paramName ? searchParams.get(paramName) : null;
  const active = paramName
    ? (urlActive && validKeys.has(urlActive) ? urlActive : fallback)
    : localActive;

  function setActive(key: string) {
    if (paramName) {
      const next = new URLSearchParams(searchParams);
      next.set(paramName, key);
      setSearchParams(next, { replace: true });
    } else {
      setLocalActive(key);
    }
  }

  return (
    <div className={className}>
      <div
        role="tablist"
        className="flex items-center gap-0.5 overflow-x-auto border-b border-[var(--color-border)]"
      >
        {tabs.map((t) => {
          const isActive = t.key === active;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`${groupId}-${t.key}`}
              aria-label={iconOnlyOnMobile && Icon ? t.label : undefined}
              title={iconOnlyOnMobile && Icon ? t.label : undefined}
              onClick={() => setActive(t.key)}
              className={cn(
                '-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-[var(--color-primary)] text-[var(--color-foreground)]'
                  : 'border-transparent text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]',
              )}
            >
              {Icon ? <Icon className="size-3.5" /> : null}
              <span className={cn(iconOnlyOnMobile && Icon && 'hidden md:inline')}>{t.label}</span>
            </button>
          );
        })}
      </div>
      {tabs.map((t) => (
        <div
          key={t.key}
          id={`${groupId}-${t.key}`}
          role="tabpanel"
          hidden={t.key !== active}
          className="pt-5"
        >
          {t.key === active ? t.content : null}
        </div>
      ))}
    </div>
  );
}
